import React, { useMemo, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { ETables } from '../../../types/enums/ETables';
import { IEditForm } from '../../../types/interfaces/IEditForm';
import { useTranslation } from 'react-i18next';
import tPaths from '../../../utils/tPaths';
import { NSCustomers } from '../../../types/interfaces/NSCustomers';
import useApi from '../../../utils/useApi';
import { endpoints } from '../../../utils/endpoints';
import useStyles from './ViewCustomer.styles';
import { Box, Button, Typography, CircularProgress } from '@material-ui/core';
import Table from '../../../components/TableWithState/Table/Table';
import useColumnsHeaders from '../../../utils/useColumnsHeaders';
import LabelWithPars from '../../../components/LabelWithPars/LabelWithPars';
import { EPaths } from '../../../types/enums/EPaths';
import InputFields from '../../../components/ItemContent/InputFields/InputFields';
import { format } from 'date-fns';
import useSnackbar from '../../../utils/useSnackbar';
import useCellStatusStyleHandler from '../../../utils/usePromocodesPanelStyles';
import { useCallbackOne } from 'use-memo-one';
import { EStatus } from '../../../types/enums/EStatus';
import { ECarrierIdentifiers } from '../../../types/enums/ECarrirerIdentifiers';
import { mutate } from 'swr';
import { ECustomerActivation } from '../../../types/enums/ECustomerActivation';

const ViewCustomer = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const styles = useStyles();

  const history = useHistory();

  const handleOnClickBack = useCallback(() => history.push(EPaths.Customers), [history]);

  const pushSnack = useSnackbar();

  const { data, post, keyForSwr, patch } = useApi<NSCustomers.IDetails>(endpoints.tableItem(ETables.Customers, id));

  const fields: IEditForm[] | undefined = useMemo(
    () =>
      data
        ? [
            {
              type: 'read',
              fieldName: 'registrationDate',
              label: t(tPaths.editFormLabels.registrationDate),
              initialValue: data.registrationDate,
            },
            {
              type: 'read',
              fieldName: 'id',
              label: t(tPaths.editFormLabels.id),
              initialValue: data._id,
            },
            {
              type: 'read',
              fieldName: 'name',
              label: t(tPaths.editFormLabels.name),
              initialValue: data.name,
            },
            {
              type: 'read',
              fieldName: 'email',
              label: t(tPaths.editFormLabels.email),
              initialValue: data.email,
            },
            {
              type: 'read',
              fieldName: 'emailVerified',
              label: t(tPaths.editFormLabels.emailVerified),
              initialValue: t(tPaths.global[data.emailVerified === ECustomerActivation.Verified ? 'yes' : 'no']),
            },
            {
              type: 'read',
              fieldName: 'athlete',
              label: t(tPaths.editFormLabels.athlete),
              initialValue: t(tPaths.global[data.athlete ? 'yes' : 'no']),
            },
            {
              type: 'read',
              fieldName: 'esimActivationAllowed',
              label: t(tPaths.editFormLabels.esimActivationAllowed),
              initialValue: t(tPaths.global[data.esimActivationAllowed ? 'yes' : 'no']),
            },
            {
              type: 'read',
              fieldName: 'carrierSubscriberReference',
              label: t(tPaths.editFormLabels.carrierSubscriberReference),
              initialValue: data.carrierSubscriberReference,
            },
            {
              type: 'read',
              fieldName: 'origin',
              label: t(tPaths.editFormLabels.origin),
              initialValue: data.origin,
            },
            {
              type: 'read',
              fieldName: 'esimNumber',
              label: t(tPaths.editFormLabels.esimNumber),
              initialValue: data.esimNumber,
            },
            {
              type: 'read',
              fieldName: 'firebaseToken',
              label: t(tPaths.editFormLabels.fcmToken),
              initialValue: data.fcmToken,
            },
            // {
            //   type: 'read',
            //   fieldName: 'balance',
            //   label: t(tPaths.editFormLabels.balance),
            //   initialValue: data.balance || 0,
            // },
            {
              type: 'read',
              fieldName: 'referralCode',
              label: t(tPaths.editFormLabels.referralCode),
              initialValue: data.referralCode,
            },
            {
              type: 'read',
              fieldName: 'usedReferralCode',
              label: t(tPaths.editFormLabels.usedReferralCode),
              initialValue: data.usedReferralCode,
            },
          ]
        : undefined,
    [data, t],
  );

  const deactivatePlanClickHandler = useCallbackOne(
    (packageId: string) => {
      return () => {
        post({
          endpoint: endpoints.disableAutoRenewPlan,
          body: { packageId },
          onSuccess: () => {
            pushSnack({ message: t(tPaths.itemContent.disableAutorenewing) });
            //TODO: enable revalidation
            mutate(
              keyForSwr,
              (response: any) => ({
                ...response,
                data: {
                  ...response?.data,
                  plans: response.data?.plans.map((plan: any) => ({
                    ...plan,
                    autoRenewing: false,
                  })),
                },
              }),
              false,
            );
          },
          onError: (err) =>
            pushSnack({
              message: `Endpoint doesn't exist`,
              //err.message
              severity: 'error',
            }),
        });
      };
    },
    [post, data, keyForSwr],
  );

  const handleOnClickResetPartnercodes = useCallbackOne(() => {
    patch({
      endpoint: endpoints.resetCodes(id),
      onSuccess: () => {
        pushSnack({ message: 'Codes was reseted', severity: 'success' });
      },
    });
  }, [id]);

  const handleOnClickResetAccounts = useCallbackOne(() => {
    patch({
      endpoint: endpoints.resetAccounts(id),
      onSuccess: () => {
        pushSnack({ message: 'Accounts was reseted', severity: 'success' });
      },
    });
  }, [id]);

  const handleOnClickResetEsimsHistory = useCallbackOne(() => {
    patch({
      endpoint: endpoints.resetEsimsHistory(id),
      onSuccess: () => {
        pushSnack({ message: 'Esims history was reseted', severity: 'success' });
      },
    });
  }, [id]);

  const plansTableData = useMemo(
    () =>
      data?.plans.map((plan) => ({
        title: plan.title,
        activationDate: plan.activationDate ? format(new Date(plan.activationDate), 'yyyy-MM-dd') : '',
        validUntil: plan.validUntil ? format(new Date(plan.validUntil), 'yyyy-MM-dd') : '',
        totalData: `${plan.totalData.amount} ${plan.totalData.dataUnit}`,
        usedData: `${plan.usedData.amount} ${plan.usedData.dataUnit}`,
        autoRenewing:
          plan.autoRenewing === true ? t(tPaths.itemContent.renewing) : t(tPaths.itemContent.renewingDisabled),
        jsxContent:
          plan.carrierIdentifier === ECarrierIdentifiers.Athlete ? (
            <Box display={'flex'} justifyContent={'right'}>
              <Button
                onClick={deactivatePlanClickHandler(plan._id)}
                className={styles.buttonDeactivatePlan}
                color="secondary"
              >
                {t(tPaths.itemContent.deactivatePlan)}
              </Button>
            </Box>
          ) : null,
      })),
    [data],
  );

  const columns = useColumnsHeaders([
    { accessorAsHeader: 'title' },
    { accessorAsHeader: 'activationDate' },
    { accessorAsHeader: 'validUntil' },
    { accessorAsHeader: 'totalData' },
    { accessorAsHeader: 'usedData' },
    { accessorAsHeader: 'autoRenewing' },
    { accessorAsHeader: 'delete' },
  ]);

  const promocodesStylesHandler = useCellStatusStyleHandler();

  const partnercodesTableData = useMemo(() => data?.partnercodes, [data]);

  const promocodesColumns = useColumnsHeaders([{ accessorAsHeader: 'code' }, { accessorAsHeader: 'status' }]);

  const promocodesTableData = useMemo(
    () =>
      data?.promocodes.map((promocode) => ({
        code: promocode.code,
        status: promocode.status === EStatus.Valid ? EStatus.Active : EStatus.Used,
      })),
    [data],
  );
  const invitedFriendsColumns = useColumnsHeaders([
    { accessorAsHeader: 'carrierSubscriberReference' },
    { accessorAsHeader: 'totalPrice' },
  ]);

  const invitedFriendsTableData: any = useMemo(() => data?.invitedFriends, [data]);

  return (
    <Box margin={2}>
      {fields ? (
        <>
          <Typography variant="h4">{t(tPaths.itemContent.viewCustomer)}</Typography>

          <Box className={styles.container} display="flex">
            <InputFields data={[fields]} />
            <Button
              children={t(tPaths.itemContent.resetPartnercodes)}
              className={styles.buttonCustomer}
              onClick={handleOnClickResetPartnercodes}
            />
            <Button
              children={t(tPaths.itemContent.resetEsimsHistory)}
              className={styles.buttonCustomer}
              onClick={handleOnClickResetEsimsHistory}
            />
            <Button
              children={t(tPaths.itemContent.resetAccounts)}
              className={styles.buttonCustomer}
              onClick={handleOnClickResetAccounts}
            />
          </Box>
          <Box className={styles.container}>
            <Box className={styles.tableContentContainer}>
              <LabelWithPars
                className={styles.tableLabel}
                label={t(tPaths.table.table(ETables.PromoCodes))}
                info={promocodesTableData?.length}
              />
              <Box className={styles.tableContainer}>
                <Table
                  columns={promocodesColumns}
                  data={promocodesTableData}
                  cellStyleHandler={promocodesStylesHandler}
                />
              </Box>
            </Box>
          </Box>
          <Box className={styles.container}>
            <Box className={styles.tableContentContainer}>
              <LabelWithPars
                className={styles.tableLabel}
                label={t(tPaths.table.table(ETables.InvitedFriends))}
                info={invitedFriendsTableData?.length}
              />
              <Box className={styles.tableContainer}>
                <Table
                  columns={invitedFriendsColumns}
                  data={invitedFriendsTableData}
                  cellStyleHandler={promocodesStylesHandler}
                />
              </Box>
            </Box>
          </Box>
          <Box className={styles.container}>
            <Box className={styles.tableContentContainer}>
              <LabelWithPars
                className={styles.tableLabel}
                label={t(tPaths.table.table(ETables.PartnerCodes))}
                info={partnercodesTableData?.length}
              />
              <Box className={styles.tableContainer}>
                <Table
                  columns={promocodesColumns}
                  data={partnercodesTableData}
                  cellStyleHandler={promocodesStylesHandler}
                />
              </Box>
            </Box>
          </Box>
          <Box className={styles.container}>
            <Box className={styles.tableContentContainer}>
              <LabelWithPars
                className={styles.tableLabel}
                label={t(tPaths.table.table(ETables.Packages))}
                info={plansTableData?.length}
              />
              <Box className={styles.tableContainer}>
                <Table
                  columns={columns}
                  data={plansTableData}
                  cellStyleHandler={(cell) => {
                    if (cell.column.id === 'title') return styles.titleCellWidth;

                    return styles.cellWidth;
                  }}
                />
              </Box>
              <Button onClick={handleOnClickBack} className={styles.buttonContainer}>
                {t(tPaths.itemContent.back)}
              </Button>
            </Box>
          </Box>
          {/* </Box> */}
        </>
      ) : (
        <CircularProgress />
      )}
    </Box>
  );
};

export default ViewCustomer;
